import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhotoThumb } from '@/components/PhotoThumb';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { animalsApi } from '../api';
import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';
import { Loader2, Save, ArrowLeft, Trash2, Star } from 'lucide-react';
import { Animal } from '../types';
import PhotosInput, { PhotoDraft } from './PhotosInput';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ChevronsUpDown, Check } from 'lucide-react';
import { env } from '@/config/env';
import { sheltersApi } from '@/features/shelters/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

const animalSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100).optional(),
  species_id: z.number({ required_error: 'Species is required' }).int().positive(),
  sex_id: z.number().int().positive().optional(),
  approx_age_months: z.number().int().min(0).optional(),
  birth_date: z.string().optional(),
  size_id: z.number().int().positive().optional(),
  color_id: z.number().int().positive().optional(),
  breed_id: z.number().int().positive().optional(),
  temperament_id: z.number().int().positive().optional(),
  // Ubicación del animal (texto simple, máx 100)
  location: z.string().max(100).optional(),
  microchip: z.string().min(3).max(64).optional(),
  behavior: z.string().max(500).optional(),
  animal_needs: z.string().max(800).optional(),
  acceptance_terms: z.string().max(800).optional(),
  good_with_animals: z.preprocess((v) => (v === null || v === undefined ? false : v), z.boolean()),
  good_with_children: z.preprocess((v) => (v === null || v === undefined ? false : v), z.boolean()),
  shelter_id: z.number().int().positive().optional(),
  status_id: z.number({ required_error: 'Status is required' }).int().positive(),
}).refine(data => {
  if (data.birth_date && new Date(data.birth_date) > new Date()) {
    return false;
  }
  return true;
}, {
  message: 'Birth date cannot be in the future',
  path: ['birth_date'],
});

type AnimalFormData = z.infer<typeof animalSchema>;

export default function AnimalFormPage() {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [selectedSpecies, setSelectedSpecies] = useState<number | undefined>();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<AnimalFormData>({
    resolver: zodResolver(animalSchema),
    mode: 'onSubmit',
    defaultValues: {
      good_with_animals: false,
      good_with_children: false,
    },
  });

  // Load lookups snapshot (ETag cached)
  const { data: snapshot, isLoading: isLookupsLoading } = useLookupsSnapshot();
  const species = snapshot?.species || [];
  const sexes = snapshot?.sexes || [];
  const sizes = snapshot?.sizes || [];
  const statuses = snapshot?.statuses || [];
  const colors = snapshot?.colors || [];
  const breedsAll = snapshot?.breeds || [];
  const temperaments = snapshot?.temperaments || [];

  // Determine adopted status
  const adoptedStatus = statuses.find(s => 
    (s.code && s.code.toLowerCase() === 'adopted') || 
    (s.label && s.label.toLowerCase().includes('adoptado'))
  );
  
  // Load animal if editing
  const { data: animalData } = useQuery({
    queryKey: ['animal', id],
    queryFn: () => animalsApi.getById(Number(id)),
    enabled: isEdit,
  });

  const isAdopted = !!(isEdit && animalData && adoptedStatus && (
    (animalData as any).status_id === adoptedStatus.id || 
    (animalData as any).statusId === adoptedStatus.id
  ));
  const adoptionInfo = (animalData as any)?.adoption;
  const adopterData = adoptionInfo?.adopter || adoptionInfo?.adopterProfile?.user || (adoptionInfo?.application?.adopterProfile?.user);

  // Shelters for lookup
  const { data: sheltersData } = useQuery({
    queryKey: ['shelters-for-animal-form'],
    queryFn: () => sheltersApi.list({ page: 1, pageSize: 500 }),
  });
  const shelterRows: any[] | undefined = Array.isArray(sheltersData) ? (sheltersData as any[]) : (sheltersData as any)?.data;
  const shelterOpts = (shelterRows || []).map((s: any) => ({ id: s.id, label: s.name }));

  // If only one shelter available, auto-select it (when not editing or when empty)
  useEffect(() => {
    if ((shelterOpts || []).length === 1) {
      const only = shelterOpts[0];
      const current = watch('shelter_id');
      if (!current) {
        setValue('shelter_id', Number(only.id), { shouldDirty: true, shouldValidate: true });
      }
    }
  }, [shelterOpts]);

  useEffect(() => {
    if (animalData) {
      const getNum = (v: any): number | undefined => (v === null || v === undefined || v === '' ? undefined : Number(v));
      const isoDate = (d: any): string | undefined => (d ? String(d).slice(0, 10) : undefined);
      const mapped: AnimalFormData = {
        name: (animalData as any).name,
        species_id: getNum((animalData as any).species_id ?? (animalData as any).speciesId)!,
        sex_id: getNum((animalData as any).sex_id ?? (animalData as any).sexId),
        approx_age_months: getNum((animalData as any).approx_age_months ?? (animalData as any).approxAgeMonths),
        birth_date: isoDate((animalData as any).birth_date ?? (animalData as any).birthDate),
        size_id: getNum((animalData as any).size_id ?? (animalData as any).sizeId),
        color_id: getNum((animalData as any).color_id ?? (animalData as any).colorId),
        breed_id: getNum((animalData as any).breed_id ?? (animalData as any).breedId),
        temperament_id: getNum((animalData as any).temperament_id ?? (animalData as any).temperamentId),
        location: (animalData as any).location,
        microchip: (animalData as any).microchip,
        behavior: (animalData as any).behavior,
        animal_needs: (animalData as any).animal_needs ?? (animalData as any).animalNeeds,
        acceptance_terms: (animalData as any).acceptance_terms ?? (animalData as any).acceptanceTerms,
        good_with_animals: Boolean(
          (animalData as any).good_with_animals ?? (animalData as any).goodWithAnimals,
        ),
        good_with_children: Boolean(
          (animalData as any).good_with_children ?? (animalData as any).goodWithChildren,
        ),
        shelter_id: getNum((animalData as any).shelter_id ?? (animalData as any).shelterId),
        status_id: getNum((animalData as any).status_id ?? (animalData as any).statusId)!,
      } as AnimalFormData;
      reset(mapped);
      setSelectedSpecies(mapped.species_id);
      const list = ((animalData as any).photos || []).map((p: any) => ({
        id: p.id,
        url: p.url,
        isPrimary: p.is_primary ?? p.isPrimary,
      }));
      setExistingPhotos(list);
      const currentPrimary = list.find((p: any) => p.isPrimary);
      if (currentPrimary) setSelectedPrimary({ type: 'existing', id: currentPrimary.id });
    }
  }, [animalData, reset]);

  // Photos state
  const [photos, setPhotos] = useState<PhotoDraft[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<any[]>([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [selectedPrimary, setSelectedPrimary] = useState<
    | { type: 'existing'; id: number }
    | { type: 'new'; index: number }
    | null
  >(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState<any | null>(null);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const [isPhotoOpen, setIsPhotoOpen] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<{ url: string; title?: string } | null>(null);
  const [lastEdited, setLastEdited] = useState<"age" | "birth" | null>(null);

  const [isDeleteAnimalOpen, setIsDeleteAnimalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Helpers to sync age (months) and birth date
  const toISODate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const dateMinusMonths = (months: number): string => {
    const now = new Date();
    const d = new Date(now.getTime());
    const targetMonth = d.getMonth() - months;
    d.setMonth(targetMonth);
    // Adjust day if month roll-over caused overflow
    if (d.getDate() !== now.getDate()) {
      d.setDate(1);
      d.setMonth(targetMonth + 1);
      d.setDate(0); // last day of previous month
    }
    return toISODate(d);
  };

  const monthsBetween = (fromISO: string): number => {
    const from = new Date(fromISO);
    const to = new Date();
    let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
    if (to.getDate() < from.getDate()) months -= 1;
    if (months < 1) months = 1;
    return months;
  };

  const ageVal = watch('approx_age_months');
  const birthVal = watch('birth_date');

  useEffect(() => {
    if (lastEdited === 'age') {
      const months = Number(ageVal) || 0;
      if (months > 0) {
        setValue('birth_date', dateMinusMonths(months), { shouldDirty: true, shouldValidate: true });
      }
      setLastEdited(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ageVal]);

  useEffect(() => {
    if (lastEdited === 'birth' && birthVal) {
      const m = monthsBetween(birthVal);
      setValue('approx_age_months', m, { shouldDirty: true, shouldValidate: true });
      setLastEdited(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [birthVal]);

  const [isSaving, setIsSaving] = useState(false);

  const toAbsolutePhotoUrl = (u?: string) => {
    if (!u) return undefined;
    if (/^https?:\/\//i.test(u)) return u;
    const base = env.apiUrl.replace(/\/$/, '');
    const path = u.replace(/^\//, '');
    return `${base}/${path}`;
  };

  const openPhoto = (url?: string, title?: string) => {
    const abs = toAbsolutePhotoUrl(url);
    if (!abs) return;
    setPhotoPreview({ url: abs, title });
    setIsPhotoOpen(true);
  };

  const onDeletePhoto = async (photoId: number): Promise<boolean> => {
    if (!isEdit) return false;
    try {
      setIsDeletingPhoto(true);
      await animalsApi.deletePhoto(Number(id), photoId);
      // refresh from backend to reflect potential primary reassignment
      const updated = await animalsApi.getWithPhotos(Number(id));
      const list = ((updated as any).photos || []).map((p: any) => ({ id: p.id, url: p.url, isPrimary: p.is_primary ?? p.isPrimary }));
      setExistingPhotos(list);
      const currentPrimary = list.find((p: any) => p.isPrimary);
      setSelectedPrimary(currentPrimary ? { type: 'existing', id: currentPrimary.id } : null);
      toast({ title: 'Photo removed' });
      return true;
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Error', description: e?.response?.data?.error?.message || 'Failed to remove photo' });
      return false;
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  const onMakePrimary = async (photoId: number) => {
    if (!isEdit || !id) return;
    try {
      await animalsApi.setPhotoPrimary(Number(id), photoId);
      setExistingPhotos((prev) => prev.map((p) => ({ ...p, isPrimary: p.id === photoId })));
      toast({ title: 'Primary photo updated' });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Error', description: e?.response?.data?.error?.message || 'Failed to update primary photo' });
    }
  };

  const onUploadNewPhotos = async () => {
    if (!isEdit || !id || photos.length === 0) return;
    try {
      setIsUploadingPhotos(true);
      const ordered = [...photos].sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1));
      const uploadRes = await animalsApi.uploadPhotos(Number(id), ordered.map((p) => p.file));
      // Merge uploaded items into existingPhotos
      const newItems: any[] = (uploadRes && uploadRes.items) || [];
      const merged = [
        ...existingPhotos,
        ...newItems.map((it: any) => ({ id: it.id, url: it.url, isPrimary: it.isPrimary ?? it.is_primary })),
      ];
      // If backend assigned primary (when none existed), reflect it; otherwise keep previous primary unless user selected a new one
      let final = merged.map((p) => ({ ...p }));
      const assigned = final.find((p) => p.isPrimary);
      if (assigned) {
        final = final.map((p) => ({ ...p, isPrimary: p.id === assigned.id }));
        setSelectedPrimary({ type: 'existing', id: assigned.id });
      }
      setExistingPhotos(final);
      setPhotos([]);
      toast({ title: 'Photos uploaded' });
    } catch (e: any) {
      toast({ variant: 'destructive', title: 'Error', description: e?.response?.data?.error?.message || 'Failed to upload photos' });
    } finally {
      setIsUploadingPhotos(false);
    }
  };

  const onDeleteAnimal = async () => {
    if (!id) return;
    try {
      setIsDeleting(true);
      await animalsApi.delete(Number(id));
      await queryClient.invalidateQueries({ queryKey: ['animals-search'] });
      await queryClient.invalidateQueries({ queryKey: ['animals'] });
      toast({ title: 'Animal eliminado correctamente' });
      navigate('/animals');
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.error?.message || 'Error al eliminar animal',
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteAnimalOpen(false);
    }
  };

  const onSubmit = async (data: AnimalFormData) => {
    const payload: any = {
      name: data.name,
      speciesId: data.species_id,
      sexId: data.sex_id,
      sizeId: data.size_id,
      colorId: data.color_id,
      breedId: data.breed_id,
      location: data.location,
      approxAgeMonths: data.approx_age_months,
      birthDate: data.birth_date,
      microchip: data.microchip,
      behavior: data.behavior,
      animalNeeds: data.animal_needs,
      acceptanceTerms: data.acceptance_terms,
      temperamentId: data.temperament_id,
      goodWithAnimals: data.good_with_animals,
      goodWithChildren: data.good_with_children,
      shelterId: data.shelter_id,
      statusId: data.status_id,
    };
    try {
      setIsSaving(true);
      const created = await (isEdit ? animalsApi.replace(Number(id), { ...payload, id: Number(id) }) : animalsApi.create(payload));
      const animalId = Number(isEdit ? id : created.id);
      if (photos.length) {
        // ensure primary is first
        const ordered = [...photos].sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1));
        await animalsApi.uploadPhotos(animalId, ordered.map((p) => p.file));
      }
      toast({ title: isEdit ? 'Animal actualizado' : 'Animal creado' });
      navigate('/animals');
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error?.response?.data?.error?.message || 'An error occurred while saving' });
    } finally {
      setIsSaving(false);
    }
  };

  const onInvalid = (formErrors: Record<string, any>) => {
    try {
      const entries = Object.entries(formErrors || {});
      const msgs = entries
        .map(([key, val]) => (val && (val as any).message ? String((val as any).message) : `${key} is invalid`))
        .filter(Boolean);
      const description = msgs.length ? msgs.join(' | ') : 'Please review the highlighted fields.';
      toast({ variant: 'destructive', title: 'Validation errors', description });
    } catch {
      toast({ variant: 'destructive', title: 'Validation errors', description: 'Form submission blocked by invalid data.' });
    }
  };

  // Generic search utilities for comboboxes
  const makeOptions = (items: any[] | undefined) => (items || []).map((i) => ({ id: i.id, label: toOptionLabel(i), raw: i }));
  const filterOptions = (opts: { id: number; label: string; raw: any }[], q: string) => {
    const s = q.trim().toLowerCase();
    if (!s) return opts.slice(0, 100);
    return opts.filter((o) => o.label.toLowerCase().includes(s)).slice(0, 100);
  };

  const speciesOpts = useMemo(() => {
    const opts = makeOptions(species);
    const priority = ['perro', 'gato'];
    return [...opts].sort((a, b) => {
      const aLower = a.label.toLowerCase();
      const bLower = b.label.toLowerCase();
      const aIdx = priority.findIndex((p) => aLower.startsWith(p));
      const bIdx = priority.findIndex((p) => bLower.startsWith(p));
      if (aIdx !== -1 || bIdx !== -1) {
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      }
      return a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
    });
  }, [species]);
  const sexOpts = useMemo(() => makeOptions(sexes), [sexes]);
  const sizeOpts = useMemo(() => {
    const order = [
      'miniatura/toy',
      'miniatura',
      'toy',
      'pequeño',
      'pequeno',
      'mediano',
      'grande',
      'gigante',
    ];
    return makeOptions(sizes).sort((a, b) => {
      const aLower = a.label.toLowerCase();
      const bLower = b.label.toLowerCase();
      const aIdx = order.findIndex((o) => aLower.startsWith(o));
      const bIdx = order.findIndex((o) => bLower.startsWith(o));
      if (aIdx !== -1 || bIdx !== -1) {
        if (aIdx === -1) return 1;
        if (bIdx === -1) return -1;
        return aIdx - bIdx;
      }
      return a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
    });
  }, [sizes]);
  const statusOpts = useMemo(() => makeOptions(statuses), [statuses]);
  const colorOpts = useMemo(() => makeOptions(colors), [colors]);
  const breedOptsAll = useMemo(() => makeOptions(breedsAll), [breedsAll]);
  const breedOpts = useMemo(() => {
    if (!selectedSpecies) return [] as typeof breedOptsAll;
    const opts = breedOptsAll.filter((o) => (o.raw.species_id ?? o.raw.speciesId) === selectedSpecies);
    // Put "mixed"/"Criollo" first if present
    const idx = opts.findIndex(
      (o) => String(o.raw?.code || '').toLowerCase() === 'mixed' || /criollo/i.test(String(o.raw?.label || o.label))
    );
    if (idx > 0) {
      const [item] = opts.splice(idx, 1);
      opts.unshift(item);
    }
    return opts;
  }, [breedOptsAll, selectedSpecies]);
  const temperamentOpts = useMemo(() => temperaments.map((i: any) => ({ id: i.id, label: toOptionLabel(i) })), [temperaments]);

  // Reusable combobox select
  function ComboboxSelect({
    value,
    onChange,
    options,
    placeholder,
    disabled,
  }: {
    value?: number | string;
    onChange: (v?: number) => void;
    options: { id: number; label: string }[];
    placeholder?: string;
    disabled?: boolean;
  }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const filtered = filterOptions(options.map((o) => ({ ...o, raw: undefined })), query);
    const valueNum = value === undefined ? undefined : Number(value);
    const current = options.find((o) => o.id === valueNum);
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between" disabled={disabled}>
            {current ? current.label : (placeholder || 'Selecciona...')}
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
          <Command>
            <CommandInput placeholder="Buscar..." value={query} onValueChange={setQuery} />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {filtered.map((o) => (
                  <CommandItem
                    key={o.id}
                    value={o.label}
                    onSelect={() => {
                      onChange(Number(o.id));
                      setOpen(false);
                    }}
                  >
                    <Check className={"mr-2 h-4 w-4 " + (o.id === valueNum ? 'opacity-100' : 'opacity-0')} />
                    {o.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {isLookupsLoading && (
          <div className="mb-4 text-sm text-muted-foreground">Loading lookups…</div>
        )}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/animals')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-4xl font-bold">
              {isAdopted && animalData ? (animalData as any).name : (isEdit ? 'Editar Animal' : 'Nuevo Animal')}
            </h1>
            <p className="text-muted-foreground">
              {isAdopted && adoptionInfo?.date
                ? `Fecha de adopción: ${new Date(adoptionInfo.date).toLocaleDateString()}`
                : (isEdit ? 'Actualiza la información del animal' : 'Registra un nuevo animal en el sistema')}
            </p>
          </div>
        </div>

        {isAdopted && existingPhotos.length > 0 && (
          <div className="mb-8 flex justify-center">
            <Carousel className="w-full max-w-lg">
              <CarouselContent>
                {existingPhotos.map((p) => (
                  <CarouselItem key={p.id}>
                    <div className="p-1">
                      <Card>
                        <div className="flex aspect-square items-center justify-center p-0 overflow-hidden rounded-md">
                          <button
                            type="button"
                            onClick={() => openPhoto(p.url, 'Foto del animal')}
                            className="w-full h-full focus:outline-none"
                            aria-label="Ver foto"
                          >
                            <PhotoThumb
                              src={toAbsolutePhotoUrl(p.url)}
                              alt="Foto del animal"
                              className="w-full h-full"
                            />
                          </button>
                        </div>
                      </Card>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>

          <fieldset disabled={isAdopted} className="contents">
          <Card className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre</Label>
                <Input id="name" {...register('name')} placeholder="Nombre del animal" />
                {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="species_id">Especie *</Label>
                <Controller
                  name="species_id"
                  control={control}
                  render={({ field }) => (
                    <ComboboxSelect
                      value={field.value}
                      onChange={(val) => {
                        field.onChange(val);
                        setSelectedSpecies(val as number);
                        setValue('breed_id', undefined);
                      }}
                      options={speciesOpts}
                      placeholder="Selecciona una especie"
                      disabled={isAdopted}
                    />
                  )}
                />
                {errors.species_id && <p className="text-sm text-destructive">{errors.species_id.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sex_id">Sexo</Label>
                <Controller
                  name="sex_id"
                  control={control}
                  render={({ field }) => (
                    <ComboboxSelect
                      value={field.value}
                      onChange={(val) => field.onChange(val as number)}
                      options={sexOpts}
                      placeholder="Selecciona un sexo"
                      disabled={isAdopted}
                    />
                  )}
                />
                {errors.sex_id && <p className="text-sm text-destructive">{errors.sex_id.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="size_id">Tamaño</Label>
                <Controller
                  name="size_id"
                  control={control}
                  render={({ field }) => (
                    <ComboboxSelect
                      value={field.value}
                      onChange={(val) => field.onChange(val as number)}
                      options={sizeOpts}
                      placeholder="Selecciona un tamaño"
                      disabled={isAdopted}
                    />
                  )}
                />
                {errors.size_id && <p className="text-sm text-destructive">{errors.size_id.message}</p>}
              </div>

              <div className="space-y-2">
                <Label>Edad Aproximada</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    value={String(Math.floor((watch('approx_age_months') || 0) / 12))}
                    onValueChange={(v) => {
                      const currentMonths = (watch('approx_age_months') || 0) % 12;
                      const total = Number(v) * 12 + currentMonths;
                      setValue('approx_age_months', total, { shouldDirty: true, shouldValidate: true });
                      setLastEdited('age');
                    }}
                    disabled={isAdopted}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Años" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 41 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {i} año{i !== 1 ? 's' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={String((watch('approx_age_months') || 0) % 12)}
                    onValueChange={(v) => {
                      const currentTotal = watch('approx_age_months') || 0;
                      const currentYears = Math.floor(currentTotal / 12);
                      const total = currentYears * 12 + Number(v);
                      setValue('approx_age_months', total, { shouldDirty: true, shouldValidate: true });
                      setLastEdited('age');
                    }}
                    disabled={isAdopted}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Meses" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {i} mes{i !== 1 ? 'es' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Hidden input to keep registration and value handling compatible with hook-form */}
                <input type="hidden" {...register('approx_age_months', { valueAsNumber: true })} />
                {errors.approx_age_months && <p className="text-sm text-destructive">{errors.approx_age_months.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="birth_date">Fecha de Nacimiento</Label>
                {(() => {
                  const field = register('birth_date');
                  return (
                    <Input
                      id="birth_date"
                      type="date"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        setLastEdited('birth');
                      }}
                    />
                  );
                })()}
                {errors.birth_date && <p className="text-sm text-destructive">{errors.birth_date.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="color_id">Color Principal</Label>
                <Controller
                  name="color_id"
                  control={control}
                  render={({ field }) => (
                    <ComboboxSelect
                      value={field.value}
                      onChange={(val) => field.onChange(val as number)}
                      options={colorOpts}
                      placeholder="Selecciona un color"
                      disabled={isAdopted}
                    />
                  )}
                />
                {errors.color_id && <p className="text-sm text-destructive">{errors.color_id.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="breed_id">Raza</Label>
                <Controller
                  name="breed_id"
                  control={control}
                  render={({ field }) => (
                    <ComboboxSelect
                      value={field.value}
                      onChange={(val) => field.onChange(val as number)}
                      options={breedOpts}
                      placeholder={selectedSpecies ? 'Selecciona una raza' : 'Primero selecciona especie'}
                      disabled={!selectedSpecies || isAdopted}
                    />
                  )}
                />
                {errors.breed_id && <p className="text-sm text-destructive">{errors.breed_id.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="temperament_id">Temperamento</Label>
                <Controller
                  name="temperament_id"
                  control={control}
                  render={({ field }) => (
                    <ComboboxSelect
                      value={field.value}
                      onChange={(val) => field.onChange(val as number)}
                      options={temperamentOpts}
                      placeholder="Selecciona un temperamento"
                      disabled={!temperamentOpts.length || isAdopted}
                    />
                  )}
                />
                {errors.temperament_id && <p className="text-sm text-destructive">{errors.temperament_id.message}</p>}
              </div>

            <div className="space-y-2">
              <Label htmlFor="microchip">Microchip</Label>
              <Input id="microchip" {...register('microchip')} placeholder="Número de microchip" />
              {errors.microchip && <p className="text-sm text-destructive">{errors.microchip.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Ubicación del animal</Label>
              <Input id="location" {...register('location')} placeholder="Ciudad, área o referencia" />
              {errors.location && <p className="text-sm text-destructive">{errors.location.message}</p>}
            </div>

              <div className="space-y-2">
                <Label htmlFor="shelter_id">Entidad</Label>
                <Controller
                  name="shelter_id"
                  control={control}
                  render={({ field }) => (
                    <ComboboxSelect
                      value={field.value}
                      onChange={(val) => field.onChange(val as number)}
                      options={shelterOpts}
                      placeholder="Selecciona una entidad"
                      disabled={isAdopted}
                    />
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status_id">Estado *</Label>
                <Controller
                  name="status_id"
                  control={control}
                  render={({ field }) => (
                    <ComboboxSelect
                      value={field.value}
                      onChange={(val) => field.onChange(val as number)}
                      options={statusOpts}
                      placeholder="Selecciona un estado"
                      disabled={isAdopted}
                    />
                  )}
                />
                {errors.status_id && <p className="text-sm text-destructive">{errors.status_id.message}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="behavior">Historia del animal</Label>
              <Textarea
                id="behavior"
                {...register('behavior')}
                placeholder="Describe la historia del animal..."
                rows={4}
              />
              {errors.behavior && <p className="text-sm text-destructive">{errors.behavior.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="animal_needs">Necesidades del animal</Label>
              <Textarea
                id="animal_needs"
                {...register('animal_needs')}
                placeholder="Cuidados, medicación, dieta especial, etc."
                rows={3}
              />
              {errors.animal_needs && <p className="text-sm text-destructive">{errors.animal_needs.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="acceptance_terms">Términos de aceptación</Label>
              <Textarea
                id="acceptance_terms"
                {...register('acceptance_terms')}
                placeholder="Condiciones o compromisos que el adoptante debe aceptar."
                rows={3}
              />
              {errors.acceptance_terms && <p className="text-sm text-destructive">{errors.acceptance_terms.message}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label htmlFor="good_with_animals">Convive bien con otros animales</Label>
                  <p className="text-sm text-muted-foreground">
                    Se lleva bien con otros animales
                  </p>
                </div>
                <Controller
                  name="good_with_animals"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id="good_with_animals"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isAdopted}
                    />
                  )}
                />
              </div>

              {/* Ocultado por requerimiento del cliente: no evaluar convivencia con niños */}
            </div>
          </Card>

          {isAdopted && adopterData && (
            <Card className="p-6 mt-6 bg-slate-50 border-blue-100">
              <h2 className="text-xl font-semibold mb-4 text-blue-800">Datos del Adoptante</h2>
              <div className="flex flex-col space-y-2">
                <div>
                  <p className="text-lg font-bold">{adopterData.name || adopterData.fullName || adopterData.full_name || '-'}</p>
                </div>
                <div>
                  <p className="text-lg">{adopterData.email || '-'}</p>
                </div>
                <div>
                  <p className="text-lg font-medium">
                    Celular: {adopterData.phone || '-'}
                  </p>
                </div>
                {adopterData.landline && (
                  <div>
                    <p className="text-lg font-medium">
                      Teléfono fijo: {adopterData.landline}
                    </p>
                  </div>
                )}
                {adopterData.address && (
                  <div>
                    <p className="text-lg font-medium">
                      Dirección: {adopterData.address} {adopterData.city ? `(${adopterData.city})` : ''}
                    </p>
                  </div>
                )}
                {adopterData.occupation && (
                  <div>
                    <p className="text-lg font-medium">
                      Ocupación: {adopterData.occupation}
                    </p>
                  </div>
                )}
                {adopterData.motivation && (
                  <div>
                    <p className="text-lg font-medium">
                      Motivación: {adopterData.motivation}
                    </p>
                  </div>
                )}
                {adoptionInfo?.date && (
                  <div className="pt-2 mt-2 border-t border-blue-200">
                    <p className="text-lg font-medium text-green-700">
                      Fecha de adopción: {new Date(adoptionInfo.date).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Photos */}
          {!isAdopted && (
            <Card className="p-6 space-y-4 mt-6">
              <h2 className="text-xl font-semibold">Fotos</h2>
              <p className="text-sm text-muted-foreground">Agrega fotos y selecciona una como principal.</p>
              {/* Existing photos with unified primary radio */}
              {isEdit && existingPhotos.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-medium">Fotos existentes</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {existingPhotos.map((p) => (
                      <div key={p.id} className="relative rounded border p-2">
                        <button
                          type="button"
                          onClick={() => openPhoto(p.url, 'Foto del animal')}
                          className="block focus:outline-none focus:ring-2 focus:ring-ring rounded"
                          aria-label="Ver foto"
                        >
                          {/* eslint-disable-next-line jsx-a11y/alt-text */}
                          <PhotoThumb src={toAbsolutePhotoUrl(p.url)} alt="Foto" className="w-full h-32 rounded" />
                        </button>
                        <div className="mt-2 flex items-center justify-between">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="radio"
                              name="primary_photo_all"
                              checked={selectedPrimary?.type === 'existing' && selectedPrimary.id === p.id}
                              onChange={() => {
                                setSelectedPrimary({ type: 'existing', id: p.id });
                                onMakePrimary(p.id);
                              }}
                            />
                            <span>Primary</span>
                          </label>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label="Eliminar foto"
                            onClick={() => {
                              setPhotoToDelete(p);
                              setIsDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <PhotosInput
                value={photos}
                onChange={setPhotos}
                radioName="primary_photo_all"
                onPrimaryChange={(idx) => setSelectedPrimary({ type: 'new', index: idx })}
              />
              
              {isEdit && photos.length > 0 && !isAdopted && (
                <div className="flex justify-end">
                  <Button type="button" onClick={onUploadNewPhotos} disabled={isUploadingPhotos}>
                    {isUploadingPhotos ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Subiendo...
                      </>
                    ) : (
                      'Subir nuevas fotos'
                    )}
                  </Button>
                </div>
              )}
            </Card>
          )}
          </fieldset>

          <div className="flex items-center justify-between mt-6">
            {isEdit && !isAdopted ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() => setIsDeleteAnimalOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar Animal
              </Button>
            ) : (
              <div /> /* Spacer if not editing */
            )}

            <div className="flex gap-4">
              <Button type="button" variant="outline" onClick={() => navigate('/animals')}>
                {isAdopted ? 'Volver' : 'Cancelar'}
              </Button>
              <Button 
                type="button" 
                onClick={handleSubmit(onSubmit, onInvalid)} 
                disabled={isSaving || isAdopted}
                className={isAdopted ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Guardar
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>

        {/* Delete Animal Confirmation Dialog */}
        <AlertDialog
          open={isDeleteDialogOpen}
          onOpenChange={(open) => {
            setIsDeleteDialogOpen(open);
            if (!open) setPhotoToDelete(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar foto?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción eliminará permanentemente la foto seleccionada.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeletingPhoto}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={async (e) => {
                  e.preventDefault();
                  if (!photoToDelete?.id) return;
                  const deleted = await onDeletePhoto(photoToDelete.id);
                  if (deleted) {
                    setIsDeleteDialogOpen(false);
                    setPhotoToDelete(null);
                  }
                }}
                disabled={isDeletingPhoto}
              >
                {isDeletingPhoto ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  'Eliminar'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={isPhotoOpen} onOpenChange={setIsPhotoOpen}>
          <DialogContent className="max-w-3xl p-2">
            <DialogHeader>
              <DialogTitle>{photoPreview?.title || 'Vista previa'}</DialogTitle>
            </DialogHeader>
            {photoPreview?.url ? (
              <img
                src={photoPreview.url}
                alt={photoPreview?.title || 'Foto del animal'}
                className="w-full max-h-[75vh] object-contain rounded"
              />
            ) : null}
          </DialogContent>
        </Dialog>

        <AlertDialog open={isDeleteAnimalOpen} onOpenChange={setIsDeleteAnimalOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Estás seguro de eliminar este animal?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción eliminará el registro del animal. Por seguridad, se realizará un borrado lógico (soft delete).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={(e) => {
                  e.preventDefault();
                  onDeleteAnimal();
                }}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  'Eliminar'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </div>
    </Layout>
  );
}
